import React from 'react';
import styled, { css } from 'styled-components';
import { useFormikContext, useField } from 'formik';
import InputMask from 'react-input-mask';
import isNil from 'lodash/isNil';

import { IInput, IStyledInput } from './types';
import { Done } from '@icons/common';
import { StyledInputContainer, InputStyles } from '../styled';
import InputHeader from '../InputHeader';
import InputError from '../InputError';
import PostfixIconWrapper from '../PostfixIconWrapper';
import InputHint from '../InputHint';

const Input: React.FC<IInput> = props => {
  const {
    className,
    name,
    placeholder = '',
    nativeInputProps = {},
    header = '',
    prependIcon,
    postfixIcon,
    postfixIconProps,
    mask,
    isMobile = false,
    hint,
    onChange,
    variant,
    renderAppendAddon,
    height,
    type,
    onBlur,
    withoutSuccessIcon,
    ...formikRest
  } = props;

  const [field] = useField({ name, ...formikRest });
  const { onChange: onChangeFormik, onBlur: onBlurFormik } = field;
  const { setFieldValue, errors, touched } = useFormikContext();

  const [focus, setFocus] = React.useState(false);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeFormik(e.target.value);
    onChange?.(e.target.value);
    setFieldValue(name, e.target.value);
  };

  const hasError = Boolean(errors[name] && touched[name]);
  const styledProps: IStyledInput = {
    hasError: hasError && !focus,
    hasTouched: Boolean(touched[name]),
    height,
  };

  const inputProps = {
    ...field,
    ...nativeInputProps,
    ...styledProps,
    id: name,
    name,
    placeholder,
    variant,
    onChange: onChangeHandler,
    onBlur: e => {
      setFocus(false);
      if (onBlur) onBlur(e);
      onBlurFormik(e);
    },
    onFocus: () => setFocus(true),
    type,
  };

  const input = isNil(mask) ? (
    <StyledInput {...inputProps} />
  ) : (
    <StyledMaskedInput
      {...inputProps}
      mask={mask}
      maskChar={null}
      value={field.value}
    />
  );

  const isCompleted =
    Boolean(!errors[name]) && Boolean(touched[name]) && Boolean(field.value);
  const isShowSuccessIcon =
    (!isNil(postfixIcon) || (!focus && isCompleted)) && !withoutSuccessIcon;

  return (
    <StyledInputContainer className={className}>
      <InputHeader
        text={header}
        prependIcon={prependIcon}
        isMobile={isMobile}
      />
      <StyledInputWrapper {...styledProps}>
        {input}
        {isShowSuccessIcon && (
          <PostfixIconWrapper {...postfixIconProps}>
            {isNil(postfixIcon) ? <DoneStyled /> : postfixIcon}
          </PostfixIconWrapper>
        )}
      </StyledInputWrapper>
      <InputError name={name} renderAppendAddon={renderAppendAddon} />
      {!isNil(hint) && !hasError && <InputHint>{hint}</InputHint>}
    </StyledInputContainer>
  );
};

const StyledInputWrapper = styled.div<IStyledInput>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
  ${InputStyles.Normal}
  ${({ disabled }) => disabled && InputStyles.Disabled};
  ${({ hasError }) => hasError && InputStyles.Error};
  ${({ height }) => height && `height: ${height}px`}
`;

const StyledInputCSS = css<IStyledInput>`
  ${InputStyles.Base}
  width: 100%;
  ::placeholder {
    ${InputStyles.Placeholder}
  }

  :-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px white inset !important;
  }
  :focus {
    ${InputStyles.Focus}
  }

  ${({ variant }) => variant === 'small' && InputStyles.Small}
`;

const StyledMaskedInput = styled(InputMask)<IStyledInput>`
  ${StyledInputCSS}
`;

const StyledInput = styled.input<IStyledInput>`
  ${StyledInputCSS}
`;

const DoneStyled = styled(Done)`
  height: 16px;
  width: 16px;

  path {
    stroke: #459e18;
  }
`;

export default Input;
